import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  CaretLeft, Play, Check, X, Barbell, Clock, ArrowUpRight, Video,
  Notepad, Trophy, Warning, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatPace, parseClock, fmtClock } from "@/lib/pace";
import { normalizeVideoUrl } from "@/lib/videoUrl";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveMedia = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

const parseIntSafe = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};
const parseFloatSafe = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/* Build initial set rows from a planned exercise. If a prior completion exists,
   use those values so the athlete can resume/re-edit. */
// Cardio / carry = time + distance (runs, rows, loaded carries). Explicit
// `mode` wins; otherwise the "conditioning" section defaults to cardio logging.
function exIsCardio(ex) {
  const m = (ex?.mode || "").toLowerCase();
  if (m === "cardio") return true;
  if (m === "strength") return false;
  return (ex?.section || "").toLowerCase() === "conditioning";
}

function buildCardio(ex, prior) {
  const planned = {
    distance: ex.distance_m ?? "",
    distance_unit: "m",
    time: ex.duration_sec ? secToClock(ex.duration_sec) : "",
    load: ex.weight_kg ?? "",
    rpe: ex.rpe ?? "",
  };
  if (prior?.intervals?.length) {
    return {
      intervals: prior.intervals.map((iv) => ({
        distance: iv.distance ?? "", distance_unit: iv.distance_unit ?? "m",
        time: iv.time ?? "", load: iv.load ?? "", rpe: iv.rpe ?? "",
      })),
    };
  }
  if (prior && (prior.result_distance != null || prior.result_time || prior.distance != null || prior.time)) {
    return {
      intervals: [{
        distance: prior.result_distance ?? prior.distance ?? planned.distance,
        distance_unit: prior.distance_unit ?? "m",
        time: prior.result_time ?? prior.time ?? planned.time,
        load: prior.result_load ?? prior.load ?? planned.load,
        rpe: prior.result_rpe ?? prior.rpe ?? planned.rpe,
      }],
    };
  }
  return { intervals: [planned] };
}

function secToClock(sec) {
  const s = parseInt(sec) || 0;
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, "0")}`;
}

function buildSets(ex, prior) {
  const priorSets = prior?.sets || [];
  const plannedCount = Math.max(1, parseIntSafe(ex.sets) || priorSets.length || 1);
  const out = [];
  for (let i = 0; i < plannedCount; i++) {
    const pri = priorSets.find((s) => s.set_number === i + 1);
    out.push({
      set_number: i + 1,
      reps: pri?.reps ?? (parseIntSafe(ex.reps) ?? ""),
      weight: pri?.weight ?? (ex.weight_kg ?? ""),
      rpe: pri?.rpe ?? (ex.rpe ?? ""),
      completed: pri?.completed ?? false,
    });
  }
  return out;
}

/* Extract a YouTube embed URL from any pasted URL. Falls back to given URL. */
function toEmbed(url) {
  if (!url) return "";
  const s = url.startsWith("/api/") ? `${BACKEND_URL}${url}` : url;
  return normalizeVideoUrl(s);
}

/* Full-screen celebratory moment shown right after an athlete logs a
   PR-setting session. CSS-only confetti; dismiss continues to /programs. */
const CONFETTI = Array.from({ length: 28 }, (_, i) => i);
const CONFETTI_COLORS = ["#FFD400", "#3B5BFF", "#FFFFFF", "#FFB800", "#7C93FF"];

function PRCelebration({ prs, onContinue }) {
  const count = prs.length;
  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4"
      data-testid="pr-celebration-overlay"
      role="dialog"
      aria-modal="true"
    >
      <style>{`
        @keyframes pr-fall {
          0% { transform: translateY(-20vh) rotate(0deg); opacity: 0; }
          10% { opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.9; }
        }
        @keyframes pr-pop {
          0% { transform: scale(0.4); opacity: 0; }
          60% { transform: scale(1.08); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
      {/* Confetti */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {CONFETTI.map((i) => (
          <span
            key={i}
            style={{
              left: `${(i * 3.5) % 100}%`,
              width: i % 3 === 0 ? 10 : 6,
              height: i % 3 === 0 ? 10 : 6,
              background: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
              animation: `pr-fall ${2.4 + (i % 5) * 0.35}s linear ${(i % 7) * 0.18}s infinite`,
              position: "absolute",
              top: 0,
              borderRadius: i % 2 ? "50%" : "1px",
            }}
          />
        ))}
      </div>

      <div
        className="relative w-full max-w-md border border-yellow-400/40 bg-brand-primary/95 p-8 text-center shadow-2xl"
        style={{ animation: "pr-pop 0.5s cubic-bezier(0.22,1,0.36,1) both" }}
      >
        <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-yellow-400/15 border border-yellow-400/40">
          <Trophy size={44} weight="fill" className="text-yellow-400 animate-bounce" />
        </div>
        <div className="label-tiny text-yellow-400 mb-1">// PERSONAL RECORD</div>
        <h2 className="font-heading text-4xl sm:text-5xl mb-2" data-testid="pr-celebration-title">
          NEW PR{count > 1 ? "S" : ""}!
        </h2>
        <p className="text-sm text-text-secondary mb-5">
          {count > 1 ? `You set ${count} new personal records this session.` : "You just set a new personal record. Big work."}
        </p>

        <div className="space-y-2 mb-6 max-h-52 overflow-y-auto no-scrollbar" data-testid="pr-celebration-list">
          {prs.map((p, i) => (
            <div
              key={p.pr_id || i}
              className="flex items-center justify-between border border-yellow-400/25 bg-yellow-400/[0.06] px-3 py-2 text-left"
              data-testid={`pr-celebration-item-${i}`}
            >
              <span className="font-heading text-sm truncate mr-3">{p.exercise}</span>
              <span className="label-tiny text-yellow-400 tabular-nums whitespace-nowrap">
                {p.value} {p.unit || ""}
              </span>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={onContinue}
          className="btn-kinetic w-full px-4 py-3 text-xs label-tiny tracking-widest"
          data-testid="pr-celebration-continue"
        >
          KEEP CRUSHING IT
        </button>
      </div>
    </div>,
    document.body
  );
}


/* Compact session-linked pre-workout readiness check-in. Optional pain fields
   stay blank ("not submitted") unless filled. Posts to the same readiness API. */
function PreWorkoutCheckin({ assignmentId, week, day }) {
  const [open, setOpen] = useState(true);
  const [done, setDone] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    sleep_hours: 7, soreness: 4, stress: 4, energy: 7,
    pain_severity: "", pain_location: "", pain_notes: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/athlete/readiness/checkin", {
        sleep_hours: parseFloat(f.sleep_hours) || 0,
        soreness: parseInt(f.soreness) || 5,
        stress: parseInt(f.stress) || 5,
        energy: parseInt(f.energy) || 5,
        pain_severity: f.pain_severity === "" ? null : parseInt(f.pain_severity),
        pain_location: f.pain_location || null,
        pain_notes: f.pain_notes || null,
        assignment_id: assignmentId,
        week: parseInt(week),
        day: parseInt(day),
      });
      setDone(true);
      toast.success("Pre-workout check-in logged");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-in failed — your entries are kept");
    } finally { setSaving(false); }
  };

  const set010 = [
    { k: "sleep_hours", label: "SLEEP (h)", min: 0, max: 12, step: 0.5 },
    { k: "soreness", label: "SORENESS (0-10)", min: 0, max: 10, step: 1 },
    { k: "stress", label: "STRESS (0-10)", min: 0, max: 10, step: 1 },
    { k: "energy", label: "ENERGY (0-10)", min: 0, max: 10, step: 1 },
  ];

  if (done) {
    return (
      <div className="mb-6 border border-green-500/40 bg-green-500/[0.05] p-3 flex items-center gap-2" data-testid="preworkout-done">
        <Check size={14} weight="bold" className="text-green-500" />
        <span className="text-sm text-green-500 label-tiny">PRE-WORKOUT CHECK-IN LOGGED · SHARED WITH YOUR COACH</span>
      </div>
    );
  }

  return (
    <div className="mb-6 border border-brand-accent/30 bg-brand-accent/[0.04] p-4" data-testid="preworkout-checkin">
      <div className="flex items-center justify-between mb-3">
        <div className="label-tiny text-brand-accent">// PRE-WORKOUT CHECK-IN (optional)</div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="label-tiny text-[10px] text-text-secondary hover:text-white" data-testid="preworkout-toggle">
          {open ? "HIDE" : "SHOW"}
        </button>
      </div>
      {open && (
        <>
          <div className="grid md:grid-cols-4 gap-4 mb-3">
            {set010.map((fld) => (
              <div key={fld.k}>
                <div className="flex items-center justify-between mb-1">
                  <label className="label-tiny">{fld.label}</label>
                  <span className="label-tiny tabular-nums text-brand-accent">{f[fld.k]}</span>
                </div>
                <input type="range" min={fld.min} max={fld.max} step={fld.step} value={f[fld.k]}
                  onChange={(e) => set(fld.k, e.target.value)} className="w-full accent-brand-accent"
                  data-testid={`preworkout-${fld.k}`} />
              </div>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="label-tiny block mb-1">PAIN SEVERITY (0-10, optional)</label>
              <Input type="number" min="0" max="10" value={f.pain_severity} placeholder="—"
                onChange={(e) => set("pain_severity", e.target.value)} className="h-9 bg-surface-elevated border-white/10" data-testid="preworkout-pain-severity" />
            </div>
            <div>
              <label className="label-tiny block mb-1">PAIN LOCATION</label>
              <Input value={f.pain_location} placeholder="e.g. left knee"
                onChange={(e) => set("pain_location", e.target.value)} className="h-9 bg-surface-elevated border-white/10" data-testid="preworkout-pain-location" />
            </div>
            <div>
              <label className="label-tiny block mb-1">PAIN NOTES</label>
              <Input value={f.pain_notes} placeholder="e.g. sharp on descent"
                onChange={(e) => set("pain_notes", e.target.value)} className="h-9 bg-surface-elevated border-white/10" data-testid="preworkout-pain-notes" />
            </div>
          </div>
          <button type="button" onClick={submit} disabled={saving}
            className="btn-kinetic px-4 py-2 text-xs label-tiny tracking-widest disabled:opacity-50" data-testid="preworkout-submit">
            {saving ? "SAVING…" : "SUBMIT CHECK-IN"}
          </button>
        </>
      )}
    </div>
  );
}


export default function SessionLogger() {
  const { assignment_id, week, day } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [rows, setRows] = useState({}); // {exerciseIndex: {sets:[], notes: string}}
  const [duration, setDuration] = useState(60);
  const [sessionNotes, setSessionNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [videoOpen, setVideoOpen] = useState(null); // { url, name }
  const [prCelebration, setPrCelebration] = useState(null); // new_prs[] after a PR-setting session
  const startedRef = React.useRef(null); // guard duplicate /start (StrictMode double-mount)

  useEffect(() => {
    api.get(`/my/sessions/${assignment_id}/w/${week}/d/${day}`)
      .then(({ data }) => {
        setSession(data);
        const initial = {};
        (data.exercises || []).forEach((ex, idx) => {
          const priorEx = (data.prior_completion?.exercises || []).find((e) => e.name === ex.name);
          if (exIsCardio(ex)) {
            initial[idx] = { sets: [], cardio: buildCardio(ex, priorEx), notes: priorEx?.notes || "" };
          } else {
            initial[idx] = { sets: buildSets(ex, priorEx), notes: priorEx?.notes || "" };
          }
        });
        setRows(initial);
        if (data.prior_completion) {
          setDuration(data.prior_completion.duration_min || 60);
          setSessionNotes(data.prior_completion.notes || "");
        } else {
          // Opening a not-yet-completed session marks it in progress (scheduled → in progress).
          const key = `${assignment_id}/${week}/${day}`;
          if (startedRef.current !== key) {
            startedRef.current = key;
            api.post(`/my/sessions/${assignment_id}/w/${week}/d/${day}/start`).catch(() => {});
          }
        }
      })
      .catch(() => toast.error("Session not found"));
  }, [assignment_id, week, day]);

  const updateSet = (exIdx, setIdx, patch) => {
    setRows((prev) => {
      const cur = prev[exIdx] || { sets: [], notes: "" };
      const nextSets = cur.sets.map((s, i) => (i === setIdx ? { ...s, ...patch } : s));
      return { ...prev, [exIdx]: { ...cur, sets: nextSets } };
    });
  };
  // Active rest timer — one per session, latest set-complete wins.
  // { exIdx, plannedRest, startedAt }
  const [restTimer, setRestTimer] = useState(null);
  const [tick, setTick] = useState(0); // re-render every 500ms while timer active
  const cuedTimerRef = React.useRef(null); // startedAt of the timer we've already cued
  useEffect(() => {
    if (!restTimer) return;
    const iv = setInterval(() => setTick((t) => t + 1), 500);
    return () => clearInterval(iv);
  }, [restTimer]);

  // Fire an audible beep + vibration exactly once when the countdown hits 0.
  useEffect(() => {
    if (!restTimer) return;
    const elapsed = Date.now() - restTimer.startedAt;
    if (elapsed < restTimer.plannedRest * 1000) return;
    if (cuedTimerRef.current === restTimer.startedAt) return;
    cuedTimerRef.current = restTimer.startedAt;
    // Web Audio soft beep — two short tones (~800Hz then ~1000Hz)
    try {
      const AudioCtor = window.AudioContext || window.webkitAudioContext;
      if (AudioCtor) {
        const ctx = new AudioCtor();
        const now = ctx.currentTime;
        [0, 0.18].forEach((offset, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = "sine";
          osc.frequency.value = i === 0 ? 800 : 1000;
          gain.gain.setValueAtTime(0.0001, now + offset);
          gain.gain.exponentialRampToValueAtTime(0.22, now + offset + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + offset + 0.14);
          osc.connect(gain).connect(ctx.destination);
          osc.start(now + offset);
          osc.stop(now + offset + 0.15);
        });
        setTimeout(() => ctx.close?.(), 500);
      }
    } catch { /* audio not available — silently ignore */ }
    // Vibration — supported on Android/Chrome. Ignored elsewhere.
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      navigator.vibrate([80, 40, 80]);
    }
  }, [restTimer, tick]);

  const toggleSet = (exIdx, setIdx) => {
    const cur = rows[exIdx];
    if (!cur) return;
    const wasCompleted = !!cur.sets[setIdx]?.completed;
    setRows((prev) => {
      const p = prev[exIdx];
      const nextSets = p.sets.map((s, i) => (i === setIdx ? { ...s, completed: !s.completed } : s));
      return { ...prev, [exIdx]: { ...p, sets: nextSets } };
    });
    if (!wasCompleted) {
      const ex = (session?.exercises || [])[exIdx];
      const rest = parseIntSafe(ex?.rest_sec);
      if (rest && rest > 0) {
        setRestTimer({ exIdx, plannedRest: rest, startedAt: Date.now() });
      }
    }
  };
  const stopRestTimer = () => setRestTimer(null);
  const addSet = (exIdx) => {
    setRows((prev) => {
      const cur = prev[exIdx];
      const last = cur.sets[cur.sets.length - 1] || {};
      return {
        ...prev,
        [exIdx]: {
          ...cur,
          sets: [...cur.sets, { set_number: cur.sets.length + 1, reps: last.reps ?? "", weight: last.weight ?? "", rpe: last.rpe ?? "", completed: false }],
        },
      };
    });
  };
  const removeSet = (exIdx, setIdx) => {
    setRows((prev) => {
      const cur = prev[exIdx];
      const filtered = cur.sets.filter((_, i) => i !== setIdx).map((s, i) => ({ ...s, set_number: i + 1 }));
      return { ...prev, [exIdx]: { ...cur, sets: filtered } };
    });
  };
  const updateExNotes = (exIdx, val) => {
    setRows((prev) => ({ ...prev, [exIdx]: { ...(prev[exIdx] || { sets: [] }), notes: val } }));
  };
  const updateCardioInterval = (exIdx, ivIdx, patch) => {
    setRows((prev) => {
      const cur = prev[exIdx] || { sets: [], cardio: { intervals: [] }, notes: "" };
      const ivs = [...(cur.cardio?.intervals || [])];
      ivs[ivIdx] = { ...(ivs[ivIdx] || {}), ...patch };
      return { ...prev, [exIdx]: { ...cur, cardio: { intervals: ivs } } };
    });
  };
  const addCardioInterval = (exIdx) => {
    setRows((prev) => {
      const cur = prev[exIdx] || { sets: [], cardio: { intervals: [] }, notes: "" };
      const ivs = [...(cur.cardio?.intervals || [])];
      const last = ivs[ivs.length - 1] || {};
      ivs.push({ distance: last.distance ?? "", distance_unit: last.distance_unit ?? "m", time: "", load: last.load ?? "", rpe: "" });
      return { ...prev, [exIdx]: { ...cur, cardio: { intervals: ivs } } };
    });
  };
  const removeCardioInterval = (exIdx, ivIdx) => {
    setRows((prev) => {
      const cur = prev[exIdx] || { sets: [], cardio: { intervals: [] }, notes: "" };
      const ivs = (cur.cardio?.intervals || []).filter((_, i) => i !== ivIdx);
      return { ...prev, [exIdx]: { ...cur, cardio: { intervals: ivs.length ? ivs : [{ distance: "", distance_unit: "m", time: "", load: "", rpe: "" }] } } };
    });
  };

  const totalPlannedSets = useMemo(() => (session?.exercises || []).reduce((sum, ex) => sum + (parseIntSafe(ex.sets) || 0), 0), [session]);
  const totalCompletedSets = useMemo(() => Object.values(rows).reduce((sum, r) => sum + (r.sets || []).filter((s) => s.completed).length, 0), [rows]);

  // Pre-warm the next exercise's demo as soon as the athlete finishes the last
  // set of the current one. Renders a tiny hidden iframe so YouTube's player,
  // JS, and thumbnail get pulled into the browser cache — when the athlete taps
  // SHOW DEMO next, it appears instantly.
  const preloadVideoUrls = useMemo(() => {
    const exs = session?.exercises || [];
    if (!exs.length) return [];
    const urls = [];
    exs.forEach((ex, idx) => {
      const cur = rows[idx];
      if (!cur) return;
      const planned = parseIntSafe(ex.sets) || 0;
      const done = (cur.sets || []).filter((s) => s.completed).length;
      // Preload once at least one set is done AND the current exercise is at
      // least 2/3 through — plenty warm-up time without wasting bandwidth on
      // exercises the athlete hasn't started.
      const closeToDone = planned > 0 && done >= Math.max(1, Math.ceil(planned * 2 / 3));
      if (!closeToDone) return;
      const next = exs[idx + 1];
      const nextVideo = next?.video_url || next?.gif_url;
      if (nextVideo) {
        const embed = toEmbed(nextVideo);
        if (embed && !/\.(mp4|webm|mov)(\?|$)/i.test(nextVideo)) {
          urls.push(embed);
        }
      }
    });
    return Array.from(new Set(urls));
  }, [session, rows]);

  const submit = async () => {
    if (!session) return;
    setSaving(true);
    try {
      const payload = {
        exercises: (session.exercises || []).map((ex, idx) => {
          const base = {
            name: ex.name,
            planned_sets: parseIntSafe(ex.sets),
            planned_reps: ex.reps,
            planned_weight: parseFloatSafe(ex.weight_kg),
            planned_rpe: parseFloatSafe(ex.rpe),
            notes: rows[idx]?.notes || null,
          };
          if (exIsCardio(ex)) {
            const ivs = rows[idx]?.cardio?.intervals || [];
            return {
              ...base,
              mode: "cardio",
              sets: [],
              intervals: ivs.map((iv) => ({
                distance: parseFloatSafe(iv.distance),
                distance_unit: iv.distance_unit || "m",
                time: iv.time || null,
                load: parseFloatSafe(iv.load),
                rpe: parseFloatSafe(iv.rpe),
              })),
            };
          }
          return {
            ...base,
            sets: (rows[idx]?.sets || []).map((s) => ({
              set_number: s.set_number,
              reps: parseIntSafe(s.reps),
              weight: parseFloatSafe(s.weight),
              rpe: parseFloatSafe(s.rpe),
              completed: !!s.completed,
            })),
          };
        }),
        duration_min: parseIntSafe(duration) || 0,
        notes: sessionNotes || null,
      };
      const { data } = await api.post(`/my/sessions/${assignment_id}/w/${week}/d/${day}/complete`, payload);
      const prs = data.new_prs || [];
      if (prs.length > 0) {
        // Give the athlete their moment before leaving the logger.
        setPrCelebration(prs);
        if (navigator.vibrate) { try { navigator.vibrate([25, 40, 25]); } catch { /* noop */ } }
      } else {
        toast.success("Session logged");
        navigate("/programs");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!session) return <div className="p-10 label-tiny text-text-secondary">Loading...</div>;

  if (session.is_rest) {
    return (
      <div className="max-w-3xl mx-auto p-10 text-center" data-testid="session-rest">
        <div className="label-tiny text-brand-accent mb-2">// PLANNED SESSION</div>
        <h1 className="font-heading text-5xl mb-3">REST DAY</h1>
        <p className="text-text-secondary">Recover fully — your coach programmed this on purpose.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 lg:p-10 pb-32" data-testid="session-logger-page">
      {prCelebration && (
        <PRCelebration prs={prCelebration} onContinue={() => navigate("/programs")} />
      )}
      <Link to="/programs" className="text-xs label-tiny text-text-secondary hover:text-white inline-flex items-center gap-1 mb-4" data-testid="session-back">
        <CaretLeft size={12} /> BACK TO PROGRAMS
      </Link>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
        <div>
          <div className="label-tiny text-brand-accent mb-1">// {session.program_name?.toUpperCase()} · W{session.week}/{session.total_weeks} · D{session.day}</div>
          <h1 className="font-heading text-4xl sm:text-5xl">{(session.session_name || "SESSION").toUpperCase()}</h1>
          <p className="text-text-secondary text-sm mt-1">
            {session.coach_name ? `Coach ${session.coach_name}` : ""}
            {session.session_type ? <span className="ml-2 label-tiny text-[10px] border border-white/15 px-1.5 py-0.5">{session.session_type.toUpperCase()}</span> : null}
            {session.prior_completion && <span className="ml-2 label-tiny text-[10px] text-green-500 border border-green-500/40 px-1.5 py-0.5">PREVIOUSLY LOGGED · EDITING</span>}
          </p>
        </div>
        <div className="text-right" data-testid="session-progress">
          <div className="label-tiny text-[9px] text-text-secondary">SETS COMPLETED</div>
          <div className="font-heading text-3xl text-brand-accent tabular-nums">{totalCompletedSets}<span className="text-lg text-text-secondary">/{totalPlannedSets}</span></div>
        </div>
      </div>

      {/* Coach feedback on this session (athlete-visible) */}
      {session.prior_completion?.coach_feedback && (
        <div className="mb-6 border border-yellow-400/40 bg-yellow-400/[0.06] p-4" data-testid="session-coach-feedback">
          <div className="label-tiny text-yellow-400 mb-1 flex items-center gap-1.5">
            <Trophy size={12} weight="fill" /> COACH FEEDBACK
            {session.prior_completion.coach_feedback_by ? ` · ${session.prior_completion.coach_feedback_by}` : ""}
          </div>
          <div className="text-sm text-white whitespace-pre-wrap">{session.prior_completion.coach_feedback}</div>
        </div>
      )}

      {/* Pre-workout readiness — session-linked; optional pain */}
      {!session.prior_completion && (
        <PreWorkoutCheckin assignmentId={assignment_id} week={week} day={day} />
      )}

      <div className="space-y-4">
        {(session.exercises || []).map((ex, idx) => (
          <ExerciseCard
            key={idx}
            idx={idx}
            ex={ex}
            state={rows[idx]}
            onSet={(setIdx, patch) => updateSet(idx, setIdx, patch)}
            onToggle={(setIdx) => toggleSet(idx, setIdx)}
            onAdd={() => addSet(idx)}
            onRemove={(setIdx) => removeSet(idx, setIdx)}
            onNotes={(v) => updateExNotes(idx, v)}
            onIntervalChange={(ivIdx, patch) => updateCardioInterval(idx, ivIdx, patch)}
            onIntervalAdd={() => addCardioInterval(idx)}
            onIntervalRemove={(ivIdx) => removeCardioInterval(idx, ivIdx)}
            openVideo={(url) => setVideoOpen({ url, name: ex.name })}
            restTimer={restTimer && restTimer.exIdx === idx ? restTimer : null}
            onStopRestTimer={stopRestTimer}
            tick={tick}
          />
        ))}
      </div>

      <div className="mt-6 border border-white/10 p-4" data-testid="session-footer-form">
        <div className="label-tiny mb-2">// SESSION SUMMARY</div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <div className="text-[10px] text-text-secondary label-tiny mb-1">DURATION (MIN)</div>
            <Input type="number" min="0" value={duration} onChange={(e) => setDuration(e.target.value)} className="h-9 bg-surface-elevated border-white/10" data-testid="session-duration" />
          </div>
        </div>
        <div className="text-[10px] text-text-secondary label-tiny mb-1">SESSION NOTES</div>
        <Textarea rows={3} value={sessionNotes} onChange={(e) => setSessionNotes(e.target.value)} className="bg-surface-elevated border-white/10" placeholder="How did it feel? Anything to flag for coach?" data-testid="session-notes" />
      </div>

      {/* Sticky submit bar with live progress fill.
          On mobile it becomes the athlete's exit target: mini progress bar +
          full-width COMPLETE SESSION so they always know how much is left. */}
      <div className="fixed bottom-0 left-0 right-0 border-t border-white/10 bg-surface-base/95 backdrop-blur-md z-30 pb-[env(safe-area-inset-bottom)]" data-testid="session-sticky-bar">
        {/* Progress fill — spans full width on top edge */}
        <div className="h-1.5 bg-white/8 relative overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-brand-accent to-brand-heat transition-all duration-300"
            style={{ width: `${Math.min(100, totalPlannedSets ? (totalCompletedSets / totalPlannedSets) * 100 : 0)}%` }}
            data-testid="session-progress-fill"
          />
        </div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center justify-between gap-3 flex-wrap sm:flex-nowrap">
          <div className="text-xs tabular-nums flex items-baseline gap-1 sm:gap-2 whitespace-nowrap" data-testid="session-progress-label">
            <span className="stat-number text-2xl">{totalCompletedSets}</span>
            <span className="text-text-secondary">/ {totalPlannedSets} sets</span>
            <span className="label-tiny text-text-secondary text-[10px] hidden sm:inline">
              · {(session.exercises || []).length} exercises
            </span>
          </div>
          <button
            onClick={submit}
            disabled={saving}
            className="btn-kinetic flex-1 sm:flex-none min-w-[160px] sm:min-w-[220px] justify-center px-6 py-3 label-tiny tracking-widest text-xs font-bold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2"
            data-testid="session-complete-btn"
          >
            <Check size={14} weight="bold" /> {saving ? "SAVING..." : session.prior_completion ? "UPDATE SESSION" : "COMPLETE SESSION"}
          </button>
        </div>
      </div>

      {videoOpen && (
        <VideoModal url={videoOpen.url} name={videoOpen.name} onClose={() => setVideoOpen(null)} />
      )}

      {/* Hidden preloader: warms the next exercise's YouTube demo in the browser
          cache so tapping SHOW DEMO on the next card is instant. */}
      {preloadVideoUrls.length > 0 && (
        <div
          aria-hidden="true"
          style={{ position: "fixed", left: -9999, top: -9999, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
          data-testid="video-preloader"
        >
          {preloadVideoUrls.map((u) => (
            <iframe key={u} src={u} title="preload" tabIndex={-1} loading="eager" width="1" height="1" />
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------- ExerciseCard -------------- */
function ExerciseCard({ idx, ex, state, onSet, onToggle, onAdd, onRemove, onNotes, onIntervalChange, onIntervalAdd, onIntervalRemove, openVideo, restTimer, onStopRestTimer, tick }) {
  const sets = state?.sets || [];
  const notes = state?.notes || "";
  const cardio = state?.cardio || { intervals: [] };
  const isCardio = exIsCardio(ex);
  const cardioIntervals = cardio.intervals || [];
  const cardioSummary = (() => {
    if (!isCardio) return null;
    const withData = cardioIntervals.filter((iv) => iv.time && iv.distance);
    if (!withData.length) return null;
    const unit = withData[0].distance_unit || "m";
    const totD = withData.reduce((s, iv) => s + (parseFloat(iv.distance) || 0), 0);
    const totS = withData.reduce((s, iv) => s + (parseClock(iv.time) || 0), 0);
    const pace = totS ? formatPace(fmtClock(totS), totD, unit) : null;
    return `${withData.length}× · ${+totD.toFixed(1)} ${unit} · ${fmtClock(totS)}${pace ? ` · avg ${pace}` : ""}`;
  })();
  const [showDetails, setShowDetails] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const prev = ex.previous;
  const video = ex.video_url || ex.gif_url;
  const videoSrc = video ? toEmbed(video) : "";
  const isFileVideo = video ? /\.(mp4|webm|mov)(\?|$)/i.test(video) : false;

  // Rest timer countdown (in seconds remaining, negative → overshoot)
  const secondsLeft = restTimer
    ? Math.max(-999, restTimer.plannedRest - Math.floor((Date.now() - restTimer.startedAt) / 1000))
    : null;
  const timerActive = restTimer && secondsLeft !== null;
  const fmtTime = (s) => {
    const abs = Math.abs(s);
    const m = Math.floor(abs / 60);
    const r = abs % 60;
    return m > 0 ? `${m}:${String(r).padStart(2, "0")}` : `${r}s`;
  };

  const planned = {
    sets: ex.sets ?? "—",
    reps: ex.reps ?? "—",
    weight: ex.weight_kg ?? null,
    rpe: ex.rpe ?? null,
    rest: ex.rest_sec ?? null,
  };

  const anyCompleted = sets.some((s) => s.completed);
  const bestCompleted = sets.filter((s) => s.completed && (s.weight != null && s.weight !== "")).sort((a, b) => (parseFloat(b.weight) || 0) - (parseFloat(a.weight) || 0))[0];
  const delta = bestCompleted && planned.weight ? parseFloat(bestCompleted.weight) - parseFloat(planned.weight) : null;
  const deltaColor = delta == null ? "" : delta >= 0 ? "text-green-500" : "text-red-500";

  return (
    <div className={`border ${anyCompleted ? "border-green-500/30 bg-green-500/[0.02]" : "border-white/10"} p-4`} data-testid={`ex-card-${idx}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-xl">{ex.name}</h3>
            {ex.section && <span className="label-tiny text-[9px] text-text-secondary border border-white/15 px-1.5 py-0.5">{ex.section.toUpperCase()}</span>}
            {video && (
              <button
                onClick={() => setShowVideo((v) => !v)}
                data-testid={`ex-video-btn-${idx}`}
                className={`inline-flex items-center gap-1 text-[10px] label-tiny transition ${showVideo ? "text-brand-heat" : "text-brand-accent hover:!text-brand-heat"}`}
                title={showVideo ? "Hide demo" : "Show demo inline"}
              >
                <Video size={10} weight="fill" /> {showVideo ? "HIDE DEMO" : "SHOW DEMO"}
              </button>
            )}
            {ex.tempo && <span className="label-tiny text-[9px] text-text-secondary">TEMPO {ex.tempo}</span>}
            {planned.rest != null && !timerActive && (
              <span className="label-tiny text-[9px] text-text-secondary inline-flex items-center gap-0.5"><Clock size={9} /> {planned.rest}s</span>
            )}
            {timerActive && (
              <button
                onClick={onStopRestTimer}
                data-testid={`ex-rest-timer-${idx}`}
                title="Tap to stop timer"
                className={`inline-flex items-center gap-1 px-2 py-0.5 border label-tiny text-[10px] tabular-nums transition-colors ${
                  secondsLeft > 0
                    ? "border-brand-accent text-brand-accent bg-brand-accent/10"
                    : "border-green-500 text-green-500 bg-green-500/10 animate-pulse"
                }`}
              >
                <Clock size={10} weight="fill" />
                {secondsLeft > 0 ? `REST ${fmtTime(secondsLeft)}` : `GO! +${fmtTime(secondsLeft)}`}
              </button>
            )}
          </div>
          {ex.coach_cue && <div className="text-xs text-text-secondary mt-1 italic">&ldquo;{ex.coach_cue}&rdquo;</div>}
        </div>
      </div>

      {/* Inline demo preview — one tap without leaving the workspace */}
      {video && showVideo && (
        <div className="mb-3 border border-brand-accent/30 bg-black/40" data-testid={`ex-video-inline-${idx}`}>
          <div className="flex items-center justify-between px-2.5 py-1.5 bg-brand-accent/10 border-b border-brand-accent/20">
            <span className="label-tiny text-[9px] text-brand-heat inline-flex items-center gap-1">
              <Video size={9} weight="fill" /> LIVE DEMO
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => openVideo(video)}
                className="label-tiny text-[9px] text-brand-accent hover:!text-brand-heat inline-flex items-center gap-1"
                data-testid={`ex-video-fullscreen-${idx}`}
                title="Open fullscreen"
              >
                <ArrowUpRight size={9} weight="bold" /> FULLSCREEN
              </button>
              <button
                onClick={() => setShowVideo(false)}
                className="text-text-secondary hover:!text-white"
                data-testid={`ex-video-close-${idx}`}
                aria-label="Hide demo"
              >
                <X size={11} />
              </button>
            </div>
          </div>
          <div className="aspect-video">
            {isFileVideo ? (
              <video src={videoSrc} controls className="w-full h-full" />
            ) : (
              <iframe src={videoSrc} title={ex.name} allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen className="w-full h-full" />
            )}
          </div>        </div>
      )}

      {isCardio ? (
        <>
          {/* Cardio / carry: planned time + distance */}
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="border border-white/10 p-2" data-testid={`ex-planned-${idx}`}>
              <div className="label-tiny text-[9px] text-brand-accent">PLANNED</div>
              <div className="text-sm font-heading tabular-nums">
                {ex.distance_m ? <span className="text-brand-accent">{ex.distance_m} m</span> : null}
                {ex.duration_sec ? <span className="text-white ml-2">{secToClock(ex.duration_sec)}</span> : null}
                {ex.weight_kg ? <span className="text-text-secondary ml-2">{ex.weight_kg} kg load</span> : null}
                {!ex.distance_m && !ex.duration_sec && !ex.weight_kg ? <span className="text-text-secondary">Log your effort</span> : null}
              </div>
            </div>
            <div className={`border ${prev ? "border-white/10" : "border-dashed border-white/10"} p-2`} data-testid={`ex-previous-${idx}`}>
              <div className="label-tiny text-[9px] text-text-secondary">PREVIOUS RESULT</div>
              {prev && (prev.distance || prev.time) ? (
                <div className="text-sm font-heading tabular-nums">
                  {prev.intervals?.length ? <span className="text-text-secondary text-xs">{prev.intervals.length}× · </span> : null}
                  {prev.distance ? `${prev.distance} ${prev.distance_unit || "m"}` : ""}
                  {prev.time ? <span className="ml-2">{prev.time}</span> : null}
                  {formatPace(prev.time, prev.distance, prev.distance_unit) && (
                    <span className="ml-2 text-[10px] text-brand-accent">· {formatPace(prev.time, prev.distance, prev.distance_unit)}</span>
                  )}
                </div>
              ) : (
                <div className="text-xs text-text-secondary italic">First time logging this exercise</div>
              )}
            </div>
          </div>
          {/* Cardio logger — one row per effort/interval (e.g. 5×500m) */}
          <div className="border border-white/10 p-3 space-y-2" data-testid={`ex-cardio-${idx}`}>
            {cardioIntervals.map((iv, ivIdx) => {
              const pace = formatPace(iv.time, iv.distance, iv.distance_unit);
              return (
                <div key={ivIdx} className="border border-white/5 p-2" data-testid={`ex-${idx}-interval-${ivIdx}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="label-tiny text-[9px] text-text-secondary">EFFORT {ivIdx + 1}</span>
                    <div className="flex items-center gap-2">
                      {pace && <span className="label-tiny text-[10px] text-brand-accent tabular-nums" data-testid={`ex-${idx}-interval-${ivIdx}-pace`}>{pace}</span>}
                      {cardioIntervals.length > 1 && (
                        <button onClick={() => onIntervalRemove(ivIdx)} className="text-text-secondary hover:text-red-400" data-testid={`ex-${idx}-interval-${ivIdx}-remove`} aria-label="Remove effort"><X size={12} /></button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    <div>
                      <label className="label-tiny text-[8px] text-text-secondary block mb-1">DISTANCE</label>
                      <div className="flex gap-1">
                        <Input type="number" inputMode="decimal" value={iv.distance ?? ""} onChange={(e) => onIntervalChange(ivIdx, { distance: e.target.value })}
                          className="h-8 bg-surface-elevated border-white/10 text-xs tabular-nums" data-testid={`ex-${idx}-interval-${ivIdx}-distance`} placeholder={ex.distance_m ? String(ex.distance_m) : "0"} />
                        <select value={iv.distance_unit || "m"} onChange={(e) => onIntervalChange(ivIdx, { distance_unit: e.target.value })}
                          className="h-8 bg-surface-elevated border border-white/10 text-xs px-1" data-testid={`ex-${idx}-interval-${ivIdx}-unit`}>
                          <option value="m">m</option><option value="km">km</option><option value="mi">mi</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="label-tiny text-[8px] text-text-secondary block mb-1">TIME (mm:ss)</label>
                      <Input value={iv.time ?? ""} onChange={(e) => onIntervalChange(ivIdx, { time: e.target.value })}
                        className="h-8 bg-surface-elevated border-white/10 text-xs tabular-nums" data-testid={`ex-${idx}-interval-${ivIdx}-time`} placeholder="2:00" />
                    </div>
                    <div>
                      <label className="label-tiny text-[8px] text-text-secondary block mb-1">LOAD (kg)</label>
                      <Input type="number" inputMode="decimal" value={iv.load ?? ""} onChange={(e) => onIntervalChange(ivIdx, { load: e.target.value })}
                        className="h-8 bg-surface-elevated border-white/10 text-xs tabular-nums" data-testid={`ex-${idx}-interval-${ivIdx}-load`} placeholder={ex.weight_kg ? String(ex.weight_kg) : "—"} />
                    </div>
                    <div>
                      <label className="label-tiny text-[8px] text-text-secondary block mb-1">RPE</label>
                      <Input type="number" step="0.5" inputMode="decimal" value={iv.rpe ?? ""} onChange={(e) => onIntervalChange(ivIdx, { rpe: e.target.value })}
                        className="h-8 bg-surface-elevated border-white/10 text-xs tabular-nums" data-testid={`ex-${idx}-interval-${ivIdx}-rpe`} placeholder="RPE" />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <button onClick={onIntervalAdd} className="label-tiny text-[10px] text-brand-accent hover:text-brand-heat inline-flex items-center gap-1" data-testid={`ex-${idx}-interval-add`}>
                <Plus size={11} weight="bold" /> ADD EFFORT
              </button>
              {cardioSummary && (
                <span className="label-tiny text-[10px] text-brand-accent tabular-nums" data-testid={`ex-${idx}-cardio-pace`}>{cardioSummary}</span>
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-2">
            <button onClick={() => setShowDetails(!showDetails)} data-testid={`ex-${idx}-toggle-notes`} className="text-[10px] label-tiny text-text-secondary hover:text-white inline-flex items-center gap-1">
              <Notepad size={10} /> {showDetails ? "HIDE" : "NOTES"}
            </button>
          </div>
        </>
      ) : (
      <>
      {/* Planned vs Previous */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <div className="border border-white/10 p-2" data-testid={`ex-planned-${idx}`}>
          <div className="label-tiny text-[9px] text-brand-accent">PLANNED</div>
          <div className="text-sm font-heading tabular-nums">
            {planned.sets} × {planned.reps}
            {planned.weight ? <span className="text-brand-accent ml-2">{planned.weight} kg</span> : null}
            {planned.rpe ? <span className="text-text-secondary ml-2">@ RPE {planned.rpe}</span> : null}
          </div>
        </div>
        <div className={`border ${prev ? "border-white/10" : "border-dashed border-white/10"} p-2`} data-testid={`ex-previous-${idx}`}>
          <div className="label-tiny text-[9px] text-text-secondary">PREVIOUS RESULT</div>
          {prev ? (
            <div className="text-sm font-heading tabular-nums">
              {prev.sets ?? "—"} × {prev.reps ?? "—"}
              {prev.weight ? <span className="text-white ml-2">{prev.weight}</span> : null}
              {prev.rpe ? <span className="text-text-secondary ml-2">@ {prev.rpe}</span> : null}
              <span className="ml-2 text-[10px] text-text-secondary">· {new Date(prev.date).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span>
            </div>
          ) : (
            <div className="text-xs text-text-secondary italic">First time logging this exercise</div>
          )}
        </div>
      </div>

      {/* Set logger */}
      <div className="border border-white/10 divide-y divide-white/5" data-testid={`ex-sets-${idx}`}>
        <div className="grid grid-cols-[36px_1fr_1fr_1fr_36px] gap-2 px-2 py-1.5 label-tiny text-[9px] text-text-secondary bg-white/[0.02]">
          <div>SET</div><div>REPS</div><div>WEIGHT</div><div>RPE</div><div></div>
        </div>
        {sets.map((s, i) => (
          <div key={i} className="grid grid-cols-[36px_1fr_1fr_1fr_36px] gap-2 px-2 py-1.5 items-center" data-testid={`ex-${idx}-set-${i}`}>
            <button
              onClick={() => onToggle(i)}
              data-testid={`ex-${idx}-set-${i}-toggle`}
              className={`w-6 h-6 flex items-center justify-center border ${s.completed ? "bg-green-500 border-green-500 text-white" : "border-white/20 text-text-secondary hover:border-white/50"}`}
              title={s.completed ? "Completed" : "Mark complete"}
            >
              {s.completed ? <Check size={12} weight="bold" /> : <span className="text-[10px]">{s.set_number}</span>}
            </button>
            <Input type="number" inputMode="numeric" value={s.reps} onChange={(e) => onSet(i, { reps: e.target.value })}
              className="h-8 bg-surface-elevated border-white/10 text-xs tabular-nums" data-testid={`ex-${idx}-set-${i}-reps`} placeholder={String(planned.reps)} />
            <Input type="number" step="0.5" inputMode="decimal" value={s.weight} onChange={(e) => onSet(i, { weight: e.target.value })}
              className="h-8 bg-surface-elevated border-white/10 text-xs tabular-nums" data-testid={`ex-${idx}-set-${i}-weight`} placeholder={planned.weight ? String(planned.weight) : "kg"} />
            <Input type="number" step="0.5" inputMode="decimal" value={s.rpe} onChange={(e) => onSet(i, { rpe: e.target.value })}
              className="h-8 bg-surface-elevated border-white/10 text-xs tabular-nums" data-testid={`ex-${idx}-set-${i}-rpe`} placeholder={planned.rpe ? String(planned.rpe) : "RPE"} />
            <button onClick={() => onRemove(i)} data-testid={`ex-${idx}-set-${i}-remove`} className="text-text-secondary hover:text-red-500"><X size={12} /></button>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between gap-2 mt-2">
        <button onClick={onAdd} data-testid={`ex-${idx}-add-set`} className="text-[10px] label-tiny text-brand-accent hover:underline">+ ADD SET</button>
        {delta != null && (
          <div className={`text-[10px] label-tiny tabular-nums ${deltaColor}`} data-testid={`ex-${idx}-delta`}>
            vs plan: {delta >= 0 ? "+" : ""}{delta.toFixed(1)} kg
          </div>
        )}
        <button onClick={() => setShowDetails(!showDetails)} data-testid={`ex-${idx}-toggle-notes`} className="text-[10px] label-tiny text-text-secondary hover:text-white inline-flex items-center gap-1">
          <Notepad size={10} /> {showDetails ? "HIDE" : "NOTES"}
        </button>
      </div>
      </>
      )}

      {showDetails && (
        <Textarea rows={2} value={notes} onChange={(e) => onNotes(e.target.value)}
          className="mt-2 bg-surface-elevated border-white/10 text-xs" placeholder="Coach cue felt off, missed a rep, etc."
          data-testid={`ex-${idx}-notes`} />
      )}
    </div>
  );
}

function VideoModal({ url, name, onClose }) {
  const src = toEmbed(url);
  const isVideoFile = /\.(mp4|webm|mov)(\?|$)/i.test(url);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onClose} data-testid="ex-video-modal">
      <div className="max-w-3xl w-full mx-4 bg-surface-elevated border border-white/10" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-3 border-b border-white/10">
          <div className="font-heading text-sm">{name}</div>
          <button onClick={onClose} className="text-text-secondary hover:text-white" data-testid="ex-video-close"><X size={14} /></button>
        </div>
        <div className="aspect-video bg-black">
          {isVideoFile ? (
            <video src={src} controls autoPlay className="w-full h-full" />
          ) : (
            <iframe src={src} title={name} allow="autoplay; encrypted-media" allowFullScreen className="w-full h-full" />
          )}
        </div>
      </div>
    </div>
  );
}
